// ── Subscription plan gating, shared by the registration and edit chats ──
//
// The business detail API drops any field the current plan does not include
// (see get_subscription_features / BusinessDetailedSerializer on the backend),
// so a question about one of those fields would only collect something the
// owner is never shown. Both chats therefore ask the plan first and skip what
// it hides. Keeping the maps here means the two flows can never drift apart.

/* Chat step id → the key the social link is stored under on the API. */
export const SOCIAL_FIELDS = {
  socialWhatsapp: "whatsapp_number",
  socialFacebook: "facebook",
  socialInstagram: "instagram",
  socialYoutube: "youtube",
  socialWebsite: "website",
  socialGoogleReview: "google_review_link",
  socialGoogleLocation: "google_location_link",
};

/* Chat step id → the plan feature flag that switches it on.
   These are the names the API returns, which for two of them differ from the
   model field ("subcategory", "google_location"). */
export const FEATURE_STEP_FLAG = {
  subCategory: "subcategory",
  about: "about_us",
  banner: "banner_image",
  socialWhatsapp: "whatsapp_number",
  socialFacebook: "facebook",
  socialInstagram: "instagram",
  socialYoutube: "youtube",
  socialWebsite: "website",
  socialGoogleReview: "google_review_link",
  socialGoogleLocation: "google_location",
};

/* Steps gated by a count rather than a boolean. A count of 0 means the plan
   displays none of them, so the whole sequence is skipped together. */
export const FEATURE_STEP_COUNT = {
  catalogueAsk: "catalogue",
  cataloguePhoto: "catalogue",
  catalogueTitle: "catalogue",
  catalogueMore: "catalogue",
  catalogueMenu: "catalogue",
  coverAsk: "cover",
  coverPhoto: "cover",
  coverMore: "cover",
  coverMenu: "cover",
  serviceAsk: "service",
  serviceName: "service",
  serviceMore: "service",
  serviceMenu: "service",
};

export const SOCIAL_STEPS = Object.keys(SOCIAL_FIELDS);

/* The shape both chats keep in state. `flags` null means "plan not read",
   which is deliberately treated as "allow everything": a failed request must
   never silently hide questions or block an edit. */
export const emptyLimits = () => ({
  catalogue: null,
  cover: null,
  service: null,
  isBasic: false,
  flags: null,
});

/* Turn the API's subscription_features object into that shape. Returns null
   when the plan has no feature row configured, so the caller can leave the
   limits untouched rather than reading an empty object as "everything off". */
export const readPlanFeatures = (features, { isBasic = false } = {}) => {
  if (!features) return null;
  return {
    /* Number.isFinite rather than `??`, so a genuine 0 survives. */
    catalogue: Number.isFinite(features.catalogue_images)
      ? features.catalogue_images
      : null,
    cover: Number.isFinite(features.cover_images)
      ? features.cover_images
      : null,
    service: Number.isFinite(features.service) ? features.service : null,
    isBasic,
    flags: features,
  };
};

/* 0 and null are different answers and must not be collapsed into "falsy":
   0 means the plan displays none, null means the plan could not be read and
   no cap is enforced at all. */
export const capReached = (cap, count) =>
  cap !== null && cap !== undefined && count >= cap;

/* Is this question worth asking on the given plan? */
export const stepAllowedFor = (limits, id) => {
  const l = limits || {};
  if (!l.flags) return true;
  const countKey = FEATURE_STEP_COUNT[id];
  /* A missing count means the plan didn't report one — assume allowed. */
  if (countKey) return (l[countKey] ?? 1) > 0;
  if (id === "socialAsk" || id === "socialMenu") {
    return SOCIAL_STEPS.some((s) => Boolean(l.flags[FEATURE_STEP_FLAG[s]]));
  }
  const flagKey = FEATURE_STEP_FLAG[id];
  /* Steps with no flag (name, category, location…) are always allowed. */
  if (!flagKey) return true;
  return Boolean(l.flags[flagKey]);
};

/* Counted features, paired with the chat step that manages them. */
export const COUNT_FEATURES = [
  { key: "catalogue_images", kind: "catalogue", step: "catalogueMenu" },
  { key: "cover_images", kind: "cover", step: "coverMenu" },
  { key: "service", kind: "service", step: "serviceMenu" },
];

/* What moving from one plan to another newly turns on.

   `steps` are the chat steps that were unavailable before and are available
   now — exactly what the upgrade chat hands to the edit chat to ask for.
   `counts` are the counted features that grew, with how many more the owner
   may now add. A feature that was already on, or a count that didn't move,
   is not "unlocked" and is left out. */
export const unlockedByUpgrade = (before, after) => {
  const steps = [];
  const counts = [];
  if (!after) return { steps, counts };
  const num = (obj, key) => (Number.isFinite(obj?.[key]) ? obj[key] : 0);

  Object.entries(FEATURE_STEP_FLAG).forEach(([step, flag]) => {
    if (!before?.[flag] && after[flag]) steps.push(step);
  });
  COUNT_FEATURES.forEach(({ key, kind, step }) => {
    const was = num(before, key);
    const now = num(after, key);
    if (now > was) {
      steps.push(step);
      counts.push({ kind, step, extra: now - was, total: now });
    }
  });
  return { steps, counts };
};
